'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useNewsStore } from '@/store/newsStore';
import { useEventStore } from '@/store/eventStore';
import { useGroupStore } from '@/store/groupStore';
import { useBlogStore } from '@/store/blogStore';
import { useUserStore } from '@/store/userStore';
import { NewsCard, NewsSidebar } from '@/components/news';
import CreateNewsModal from '@/components/ui/CreateNewsModal';
import { useLanguage } from '@/context/LanguageContext';

export function NewsFeed() {
  const { currentUser, isFollowingGroup } = useAuth();
  const { t } = useLanguage();
  const { news, loading, hasMore, currentPage, fetchNews, fetchCommentsBatch, getSortedNews } = useNewsStore();
  const { upcomingEvents, fetchUpcomingEvents } = useEventStore();
  const { groups, fetchGroups } = useGroupStore();
  const { fetchBlogs, getPopularBlogs } = useBlogStore();
  const { users, fetchUsers } = useUserStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  const observerTarget = useRef<HTMLDivElement>(null);

  // Fetch public data
  useEffect(() => {
    fetchNews(1);
    fetchUpcomingEvents();
    fetchGroups();
    fetchBlogs(1);
  }, [fetchNews, fetchUpcomingEvents, fetchGroups, fetchBlogs]);

  // Fetch auth-gated data
  useEffect(() => {
    if (!currentUser) return;
    fetchUsers();
  }, [currentUser, fetchUsers]);

  // Fetch comments for all visible news posts in one request.
  useEffect(() => {
    void fetchCommentsBatch(news.map((post) => post.documentId));
  }, [news, fetchCommentsBatch]);

  // Handle hash navigation and highlight
  useEffect(() => {
    if (typeof window === 'undefined' || news.length === 0) return;

    const hash = window.location.hash;
    if (!hash) return;

    const timeoutId = setTimeout(() => {
      const element = document.querySelector(hash);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');

        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
        }, 3000);

        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname);
        }, 500);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [news.length]);

  // Infinite scroll with Intersection Observer
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    if (target.isIntersecting && hasMore && !loading) {
      fetchNews(currentPage + 1);
    }
  }, [hasMore, loading, currentPage, fetchNews]);

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    });

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  const popularGroups = [...groups]
    .filter(g => !g.isArchived)
    .sort((a, b) => (b.memberCount ?? 0) - (a.memberCount ?? 0))
    .slice(0, 3);

  const popularArticles = getPopularBlogs();

  const handleModalResult = (success: boolean, message: string) => {
    setToast({
      show: true,
      message,
      type: success ? 'success' : 'error',
    });

    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  return (
    <div className="min-h-screen">
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div
            className={`px-4 py-3 rounded-lg shadow-lg ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}

      <div className="container p-3 md:p-6">
        <header className="mb-6">
          <h1 className="text-[48px] leading-tight text-primary" data-testid="news-feed-heading" style={{ fontFamily: 'var(--font-abhaya-libre)' }}>{t.nav.newsFeed}</h1>
        </header>
        <div className="flex gap-6">
          <main className="flex-1 min-w-0 max-w-full">
            <div className="space-y-4">
              {loading && news.length === 0 ? (
                <div className="text-center py-12" data-testid="news-feed-loading">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="mt-4 text-foreground-muted">{t.news.loadingNews}</p>
                </div>
              ) : news.length === 0 ? (
                <div className="text-center py-12 bg-background border border-border rounded-lg" data-testid="news-feed-empty-state">
                  <p className="text-foreground-muted mb-4">{t.news.noNews}</p>
                  {currentUser && (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="news-feed-create-first-btn"
                    >
                      {t.news.createFirst}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {getSortedNews(isFollowingGroup).map((newsItem) => (
                    <NewsCard
                      key={newsItem.documentId}
                      news={newsItem}
                    />
                  ))}

                  <div ref={observerTarget} className="h-10 flex items-center justify-center" data-testid="news-feed-infinite-scroll-sentinel">
                    {loading && (
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    )}
                  </div>

                  {!hasMore && news.length > 0 && (
                    <p className="text-center text-foreground-muted py-4" data-testid="news-feed-reached-end">
                      {t.news.reachedEnd}
                    </p>
                  )}
                </>
              )}
            </div>
          </main>

          <NewsSidebar
            upcomingEvents={upcomingEvents}
            popularGroups={popularGroups}
            popularArticles={popularArticles}
            users={users}
          />
        </div>
      </div>

      {showCreateModal && (
        <CreateNewsModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onResult={handleModalResult}
        />
      )}
    </div>
  );
}
